import clsx from "clsx";
import { useState } from "react";

import type { ToolUsageRow } from "@/lib/types";

import { formatDuration, formatInt } from "./formatters";

// ---------------------------------------------------------------------------
// Grouping logic — works for any "prefix:" tool pattern (bash:*, skill:*, …)
// ---------------------------------------------------------------------------

/** Prefixes that should be grouped into collapsible parent rows. */
const GROUP_PREFIXES = ["bash:", "skill:"] as const;

type ToolGroup = {
  /** The group name used as the parent row label, e.g. "bash" or "skill" */
  name: string;
  /** The prefix string including the colon, e.g. "bash:" */
  prefix: string;
  /** Aggregated parent row */
  parent: ToolUsageRow;
  /** Individual sub-category rows sorted by calls desc */
  subRows: ToolUsageRow[];
};

function groupToolRows(data: ToolUsageRow[]): {
  regular: ToolUsageRow[];
  groups: ToolGroup[];
} {
  const regular: ToolUsageRow[] = [];
  const buckets: Record<string, ToolUsageRow[]> = {};

  for (const row of data) {
    const prefix = GROUP_PREFIXES.find((p) => row.tool.startsWith(p));
    if (prefix) {
      (buckets[prefix] ??= []).push(row);
    } else {
      regular.push(row);
    }
  }

  const groups: ToolGroup[] = [];

  for (const prefix of GROUP_PREFIXES) {
    const children = buckets[prefix];
    if (!children || children.length === 0) continue;

    const totalCalls = children.reduce((sum, r) => sum + r.calls, 0);
    const totalErrors = children.reduce((sum, r) => sum + r.errors, 0);
    const totalDurationWeighted = children.reduce((sum, r) => sum + r.avgDurationMs * r.calls, 0);

    children.sort((a, b) => b.calls - a.calls);

    groups.push({
      name: prefix.slice(0, -1), // "bash:" → "bash"
      prefix,
      parent: {
        tool: prefix.slice(0, -1),
        calls: totalCalls,
        errors: totalErrors,
        errorRate: totalCalls > 0 ? Math.round((totalErrors / totalCalls) * 1000) / 10 : 0,
        avgDurationMs: totalCalls > 0 ? Math.round(totalDurationWeighted / totalCalls) : 0,
      },
      subRows: children,
    });
  }

  return { regular, groups };
}

// ---------------------------------------------------------------------------
// Row components
// ---------------------------------------------------------------------------

function ToolRow({ row, prefix }: { row: ToolUsageRow; prefix?: string }) {
  const indent = !!prefix;
  const displayName = prefix ? row.tool.slice(prefix.length) : row.tool;

  return (
    <tr className="border-b border-cyber-border/20 transition-colors hover:bg-cyber-surface/30">
      <td className={clsx("py-1.5 pr-4 font-mono text-cyber-text", indent && "pl-5")}>
        {indent && <span className="mr-1 text-cyber-muted/40">{">"}</span>}
        {displayName}
      </td>
      <td className="py-1.5 pr-4 text-right text-cyber-muted">{formatInt(row.calls)}</td>
      <td className="py-1.5 pr-4 text-right text-cyber-muted">{formatInt(row.errors)}</td>
      <td
        className={clsx(
          "py-1.5 pr-4 text-right",
          row.errorRate > 20 ? "font-medium text-cyber-red" : "text-cyber-muted",
        )}
      >
        {row.errorRate}%
      </td>
      <td className="py-1.5 text-right text-cyber-muted">{formatDuration(row.avgDurationMs)}</td>
    </tr>
  );
}

function GroupRows({
  group,
  expanded,
  onToggle,
}: {
  group: ToolGroup;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { parent, subRows, name, prefix } = group;

  return (
    <>
      {/* Parent row — clickable toggle */}
      <tr
        className="cursor-pointer border-b border-cyber-border/20 transition-colors hover:bg-cyber-surface/30"
        onClick={onToggle}
      >
        <td className="py-1.5 pr-4 font-mono text-cyber-text">
          <span className="mr-1 inline-block w-3 text-center text-cyber-muted/60">
            {expanded ? "-" : "+"}
          </span>
          {name}
          <span className="ml-1.5 text-[10px] text-cyber-muted/50">
            ({subRows.length} sub-categories)
          </span>
        </td>
        <td className="py-1.5 pr-4 text-right text-cyber-muted">{formatInt(parent.calls)}</td>
        <td className="py-1.5 pr-4 text-right text-cyber-muted">{formatInt(parent.errors)}</td>
        <td
          className={clsx(
            "py-1.5 pr-4 text-right",
            parent.errorRate > 20 ? "font-medium text-red-400" : "text-cyber-muted",
          )}
        >
          {parent.errorRate}%
        </td>
        <td className="py-1.5 text-right text-cyber-muted">
          {formatDuration(parent.avgDurationMs)}
        </td>
      </tr>

      {/* Indented children */}
      {expanded && subRows.map((row) => <ToolRow key={row.tool} row={row} prefix={prefix} />)}
    </>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ToolUsageTable({ data }: { data: ToolUsageRow[] }) {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  if (data.length === 0) {
    return <p className="text-xs text-cyber-muted/60">No tool usage data available.</p>;
  }

  const { regular, groups } = groupToolRows(data);

  // Build group lookup for rendering
  const groupByName = new Map<string, ToolGroup>();
  for (const g of groups) groupByName.set(g.name, g);

  // Merge regular rows + group parent rows, sorted by calls descending
  const sortedRows: ToolUsageRow[] = [...regular];
  for (const g of groups) sortedRows.push(g.parent);
  sortedRows.sort((a, b) => b.calls - a.calls);

  const toggleGroup = (name: string) =>
    setExpandedGroups((prev) => ({ ...prev, [name]: !prev[name] }));

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-cyber-border/50 text-left text-[10px] uppercase tracking-wide text-cyber-muted/70">
            <th className="pb-2 pr-4 font-medium">Tool</th>
            <th className="pb-2 pr-4 text-right font-medium">Calls</th>
            <th className="pb-2 pr-4 text-right font-medium">Errors</th>
            <th className="pb-2 pr-4 text-right font-medium">Error %</th>
            <th className="pb-2 text-right font-medium">Avg Duration</th>
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => {
            const group = groupByName.get(row.tool);
            return group ? (
              <GroupRows
                key={group.name}
                group={group}
                expanded={!!expandedGroups[group.name]}
                onToggle={() => toggleGroup(group.name)}
              />
            ) : (
              <ToolRow key={row.tool} row={row} />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
